/*
  The finish breakdown above the card tables (§6.2, §6.3), off MUI and off Emotion.

  This is the one table in the feature that does NOT take a fixed column descriptor. Its
  columns are a pivot — one per grade that actually occurs in the rows handed in — so
  there is no fixed list to describe, and with no sort and no filter there is nothing for
  DataTable's controller to drive. What it does take is everything §6.2 asks of a table:
  the shared TABLE_CELL_PADDING density (through the shared cell classes), zebra rows, a
  hover row, a focus ring, a sticky header, and its own overflow-x container so a wide
  pivot scrolls in here rather than sideways-scrolling the page body (§6.3).

  **It stacks below md, like every other table with a header row.** §6.1's matrix marks it
  Responsive = yes; §6.3 exempts exactly one table — GradeReportView's cert table, and only
  because it has no header row and so has no column name to promote. This one has a header
  row, so the exemption does not reach it. The cards are DataTableStackedList's, fed
  descriptors built from the same pivot output the <thead> uses, so there is one stacked
  look in the feature rather than two that can drift.

  Two intended changes from the MUI version:

  - **The counts are right-aligned and en-US formatted** (§6.2), where every cell used to
    be centred and raw. Same rule the descriptor applies to a `numeric` column; this table
    applies it by hand because it has no descriptor.
  - **Nothing renders when there are no rows.** The old table painted an empty bordered
    box with two headers and no body while the gradings query was in flight, which §6.2
    calls out as a bare blank table. The pivot has no loading or error state of its own —
    it is handed rows the view already fetched — so absent is the honest rendering.

  The pivot itself is a pure function, exported and unit-tested: component tests cannot
  run here (RTL 13 vs React 17, §7.5.2), so the counting has to be reachable without one.
*/

import { formatCount } from "lib";

import {
  ALIGN_CLASS,
  DataTableColumn,
  DataTableStackedList,
  TABLE_BASE,
  TABLE_BODY_CELL,
  TABLE_HEAD_CELL,
  TABLE_ROW_FOCUS,
  TABLE_ROW_HOVER,
  TABLE_ROW_ZEBRA,
  TABLE_SCROLL_AREA,
  useIsStacked,
} from "./DataTable";

type FinishTableProps = {
  data: { finish: string; grade: string }[];
};

/* One accessible name for both layouts: the caption, the region and the stacked list. */
const FINISH_TABLE_LABEL = "Grades by finish";

/*
  The API's grade text mapped to the column heading it counts under. Insertion order is
  the column order: best grade first.
*/
const gradeMapping: { [key: string]: string } = {
  /*
    "Flawless", not "Gold 10.0".

    These keys are matched against `grade` on a GRADING, and gradings carry the grade's
    NAME. "Gold 10.0" is the value the same grade takes in a set's `finalGradeCounts`,
    which is a different field on a different endpoint — this table was reading one
    vocabulary with the other one's key, so the top grade in the scale matched nothing and
    its column silently never appeared.

    It is not a hypothetical: cert 000186853's card, Hop's Trevenant 204/193, has exactly
    one Flawless among its 1,532 gradings, and the set page shows that 1 in its "Flawless
    10 (Quad)" column while this table showed no such column at all. "Gold 10.0" is kept
    as a second key so a row carrying either spelling still lands in the right column.
  */
  "Flawless": "Flawless 10 (Quad)",
  "Gold 10.0": "Flawless 10 (Quad)",
  "Pristine": "Pristine 10",
  "Gem Mint 10": "Gem Mint 10",
  "Gem Mint": "9.5",
  "Mint": "9",
  "NM-Mint+": "8.5",
  "NM-Mint": "8",
  "Near Mint+": "7.5",
  "Near Mint": "7",
  "Excellent": "6",
  "Good": "5",
  "Fair": "4",
  "Worn": "3",
  "Poor": "2",
  "Damaged": "1",
  "Authentic": "A"
};

export type FinishPivotRow = {
  finish: string;
  /* Every grading of this finish, including any whose grade text is not in the map. */
  total: number;
  /* Keyed by column heading, filled for every heading in `columns`. */
  counts: { [heading: string]: number };
};

export type FinishPivot = {
  /* Every heading on the scale, best first; none when there are no rows. */
  columns: string[];
  rows: FinishPivotRow[];
};

/*
  Count gradings per finish, then per grade.

  Behaviour carried over unchanged from the MUI version, deliberately: a grading whose
  grade text is not in the map (task 9 found `grade` is nullable in live data, and the
  type says otherwise) still counts towards its finish's total but lands in no grade
  column. Dropping it from the total instead would make the row stop adding up against
  the card table below it.
*/
export const buildFinishPivot = (
  data: { finish: string; grade: string }[]
): FinishPivot => {
  /*
    De-duplicated: two grade spellings now share the "Flawless 10 (Quad)" heading, and a
    repeated heading would build a duplicate column and a duplicate React key.
  */
  const headings = Array.from(
    new Set(Object.keys(gradeMapping).map(grade => gradeMapping[grade]))
  );
  const byFinish = new Map<string, FinishPivotRow>();

  data.forEach(({ finish, grade }) => {
    let row = byFinish.get(finish);
    if (!row) {
      const counts: { [heading: string]: number } = {};
      headings.forEach(heading => {
        counts[heading] = 0;
      });
      row = { finish, total: 0, counts };
      byFinish.set(finish, row);
    }
    row.total += 1;

    const heading = gradeMapping[grade];
    if (heading !== undefined) {
      row.counts[heading] += 1;
    }
  });

  /*
    The whole scale, every time, zeros included (Robert, 2026-10-21: "make all the grades
    fit across this line, from 6 down is cut off"). It used to keep only the grades that
    occur, so a card with nothing below 6 lost 5 to A off the right of the row and read as
    cut off. The set page's table already shows all sixteen; this one now matches it.
  */
  const rows = Array.from(byFinish.values());
  const columns = rows.length > 0 ? headings : [];

  return { columns, rows };
};

/*
  The phone's stacked cards keep only the grades that occur (D3: phones keep the cards they
  have now). The whole scale is for the desktop row, where an empty column holds its place
  on the line; a card listing ten zeros on a 390px screen only buries the real counts.
*/
export const occurringColumns = ({ columns, rows }: FinishPivot): string[] =>
  columns.filter((heading) => rows.some((row) => row.counts[heading] > 0));

/*
  The stacked cards are DataTableStackedList's, so there is one stacked look in the
  feature rather than two that can drift. The descriptors are built from the same pivot
  output the <thead> uses, so a column can never appear in one layout and not the other.

  Every column is `sortable: false`: sortValue is here only because a column with no
  render displays it (formatCount()'d when numeric), and §6.1 gives this table no sort.
*/
const stackedColumns = (
  headings: readonly string[]
): DataTableColumn<FinishPivotRow>[] => [
  {
    id: "finish",
    label: "Finish",
    sortValue: (row) => row.finish,
    sortable: false
  },
  {
    id: "total",
    label: "Total Grades",
    numeric: true,
    sortValue: (row) => row.total,
    sortable: false
  },
  ...headings.map((heading) => ({
    id: heading,
    label: heading,
    numeric: true,
    sortValue: (row: FinishPivotRow) => row.counts[heading],
    sortable: false
  }))
];

export const FinishTable = ({ data }: FinishTableProps) => {
  const isStacked = useIsStacked();
  const pivot = buildFinishPivot(data);
  const { columns, rows } = pivot;

  if (rows.length === 0) {
    return null;
  }

  if (isStacked) {
    return (
      <div className="flex flex-col gap-3">
        <h2 className="sr-only">{FINISH_TABLE_LABEL}</h2>
        <DataTableStackedList
          columns={stackedColumns(occurringColumns(pivot))}
          rows={rows}
          getRowKey={(row) => row.finish}
          label={FINISH_TABLE_LABEL}
        />
      </div>
    );
  }

  return (
    <div
      /*
        Reachable and scrollable by keyboard: a region only a mouse can scroll is a WCAG
        failure. Same treatment DataTable gives its own scroll container.
      */
      role="region"
      aria-label={FINISH_TABLE_LABEL}
      tabIndex={0}
      className={TABLE_SCROLL_AREA}
    >
      <table className={TABLE_BASE}>
        <caption className="sr-only">{FINISH_TABLE_LABEL}</caption>
        <thead>
          <tr>
            <th scope="col" className={TABLE_HEAD_CELL}>
              Finish
            </th>
            <th scope="col" className={`${TABLE_HEAD_CELL} ${ALIGN_CLASS.right}`}>
              Total Grades
            </th>
            {columns.map(heading => (
              <th key={heading} scope="col" className={`${TABLE_HEAD_CELL} ${ALIGN_CLASS.right}`}>
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr
              key={row.finish}
              className={`${TABLE_ROW_ZEBRA} ${TABLE_ROW_HOVER} ${TABLE_ROW_FOCUS}`}
            >
              <th
                scope="row"
                className={`${TABLE_BODY_CELL} whitespace-nowrap font-medium`}
              >
                {row.finish}
              </th>
              <td className={`${TABLE_BODY_CELL} ${ALIGN_CLASS.right}`}>
                {formatCount(row.total)}
              </td>
              {columns.map(heading => (
                <td
                  key={heading}
                  className={`${TABLE_BODY_CELL} ${ALIGN_CLASS.right}`}
                >
                  {formatCount(row.counts[heading])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
